import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faBolt, faMoneyBillWave, faBalanceScale, faPercentage,
  faFilter, faSyncAlt, faUserCircle, faCaretDown, faUser,
  faSignOutAlt, faCrown, faAngleDoubleLeft, faAngleLeft,
  faAngleRight, faAngleDoubleRight, faInfoCircle,
  faCheckCircle, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Line, Bar, Scatter } from 'react-chartjs-2';
import '../scss/EnergyMonitoring.scss';
import BaseURL from '../assets/contants/BaseURL';

ChartJS.register(...registerables);

const EnergyMonitoring = () => {
  const navigate = useNavigate();
  
  // State variables
  const [cranes, setCranes] = useState([]);
  const [selectedCrane, setSelectedCrane] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('today');
  
  // State for applied filters (used for actual data fetching)
  const [appliedCrane, setAppliedCrane] = useState('');
  const [appliedDateRange, setAppliedDateRange] = useState('today');
  
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({ overall: 'disconnected', message: 'Connecting...' });
  const [notification, setNotification] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);
  
  // Summary data state
  const [summaryData, setSummaryData] = useState({
    totalPower: 0,
    totalWh: 0,
    totalCost: 0,
    systemEfficiency: 0,
    energyPerTon: 0
  });
  
  // Chart data states
  const [powerChartData, setPowerChartData] = useState({ labels: [], datasets: [] });
  const [currentChartData, setCurrentChartData] = useState({ labels: [], datasets: [] });
  const [voltageChartData, setVoltageChartData] = useState({ labels: [], datasets: [] });
  const [costChartData, setCostChartData] = useState({ labels: [], datasets: [] });
  const [energyLoadChartData, setEnergyLoadChartData] = useState({ datasets: [] });
  const [costChartInterval, setCostChartInterval] = useState('Hourly Data');
  
  // Table data state
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage] = useState(10);
  
  // Refs to store current values for use in WebSocket callbacks without causing reconnections
  const currentPageRef = useRef(currentPage);
  const appliedCraneRef = useRef(appliedCrane);
  const appliedDateRangeRef = useRef(appliedDateRange);
  const rowsPerPageRef = useRef(rowsPerPage);
  
  // Refs to track if connections are already established
  const wsConnections = useRef({
    summary: null,
    powerChart: null,
    currentChart: null,
    voltageChart: null,
    costChart: null,
    energyLoadChart: null,
    table: null
  });
  
  const isConnecting = useRef(false);
  const isUnmounting = useRef(false);
  const initialLoadDone = useRef(false);
  
  // Chart refs for updating
  const powerChartRef = useRef();
  const currentChartRef = useRef();
  const voltageChartRef = useRef();
  const costChartRef = useRef();
  const energyLoadChartRef = useRef();

  // Update refs when values change
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    appliedCraneRef.current = appliedCrane;
  }, [appliedCrane]);

  useEffect(() => {
    appliedDateRangeRef.current = appliedDateRange;
  }, [appliedDateRange]);
  
  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');
  const getSessionID = () => sessionStorage.getItem('session_id') || localStorage.getItem('session_id');

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);
  
  const getWebSocketHost = () => {
    let host = BaseURL;
    if (BaseURL.includes('://')) {
      host = BaseURL.split('://')[1];
    }
    if (host.endsWith('/')) {
      host = host.slice(0, -1);
    }
    return host;
  };
  
  const getWebSocketUrl = (endpoint) => {
    const token = getAuthToken();
    const session_id = getSessionID();
    
    if (!token || !session_id) {
      console.warn(`Missing auth credentials for ${endpoint}`);
      return null;
    }
    
    const host = getWebSocketHost();
    const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${wsScheme}//${host}/data/crane/energy/${endpoint}/?token=${encodeURIComponent(token)}&session_id=${encodeURIComponent(session_id)}`;
  };
  
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };
  
  const fetchCranes = useCallback(async () => {
    const token = getAuthToken();
    
    if (!token) {
      console.error('No authentication token found');
      return;
    }
    
    try {
      const response = await fetch(`${BaseURL}meters/cms-cranes/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.status === 401) {
        showNotification('Session expired. Please login again.', 'error');
        setTimeout(() => navigate('/login'), 1500);
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setCranes(Array.isArray(data) ? data : []);
        if (data.length > 0 && !initialLoadDone.current) {
          const firstCrane = data[0].machine_id;
          setSelectedCrane(firstCrane);
          // Set applied filters for initial load
          setAppliedCrane(firstCrane);
          setAppliedDateRange('today');
        }
      }
    } catch (error) {
      console.error('Error fetching cranes:', error);
      showNotification('Failed to load cranes', 'error');
    }
  }, [navigate]);
  
  const disconnectAllWebSockets = useCallback(() => {
    Object.keys(wsConnections.current).forEach(key => {
      if (wsConnections.current[key]) {
        if (wsConnections.current[key].readyState === WebSocket.OPEN) {
          try {
            wsConnections.current[key].send(JSON.stringify({ type: 'unsubscribe' }));
          } catch (e) {
            console.warn(`Error unsubscribing from ${key}:`, e);
          }
          wsConnections.current[key].close(1000, 'User disconnected');
        }
        wsConnections.current[key] = null;
      }
    });
    isConnecting.current = false;
  }, []);
  
  const connectWebSocket = useCallback((connectionName, endpoint, craneId, dateRange) => {
    // Don't connect if already connecting or if we already have an open connection
    if (wsConnections.current[connectionName]?.readyState === WebSocket.OPEN) {
      console.log(`${connectionName} already connected, skipping`);
      return;
    }
    
    const wsUrl = getWebSocketUrl(endpoint);
    if (!wsUrl) {
      console.warn(`No WebSocket URL for ${connectionName}`);
      return;
    }
    
    console.log(`Connecting ${connectionName} WebSocket`);
    
    const ws = new WebSocket(wsUrl);
    wsConnections.current[connectionName] = ws;
    
    const timeout = setTimeout(() => {
      if (ws.readyState !== WebSocket.OPEN) {
        console.error(`${connectionName} WebSocket connection timeout`);
        ws.close();
      }
    }, 10000);
    
    ws.onopen = () => {
      clearTimeout(timeout);
      console.log(`${connectionName} WebSocket connected`);
      
      try {
        ws.send(JSON.stringify({ type: 'subscribe', machine_id: craneId }));
        ws.send(JSON.stringify({ type: 'update_date_range', date_range: dateRange }));
        ws.send(JSON.stringify({ type: 'get_connection_status' }));
        
        if (connectionName === 'table') {
          ws.send(JSON.stringify({ 
            type: 'change_page', 
            page: currentPageRef.current, 
            page_size: rowsPerPageRef.current 
          }));
        }
      } catch (error) {
        console.error(`Error sending initial ${connectionName} messages:`, error);
      }
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pong') return;
        handleWebSocketMessage(connectionName, data);
      } catch (error) {
        console.error(`Error parsing ${connectionName} message:`, error);
      }
    };
    
    ws.onclose = (event) => {
      clearTimeout(timeout);
      console.log(`${connectionName} WebSocket disconnected: ${event.code}`);
      wsConnections.current[connectionName] = null;
      
      // Only reconnect if not unmounting and not a normal closure
      if (!isUnmounting.current && appliedCraneRef.current && event.code !== 1000) {
        setTimeout(() => {
          if (!isUnmounting.current && appliedCraneRef.current && !wsConnections.current[connectionName]) {
            connectWebSocket(connectionName, endpoint, appliedCraneRef.current, appliedDateRangeRef.current);
          }
        }, 5000);
      }
    };
    
    ws.onerror = (error) => {
      clearTimeout(timeout);
      console.error(`${connectionName} WebSocket error:`, error);
    };
  }, []);
  
  const connectAllWebSockets = useCallback((craneId, dateRange) => {
    if (isConnecting.current) {
      console.log('Already connecting, skipping...');
      return;
    }
    
    isConnecting.current = true;
    
    const endpoints = {
      summary: 'summary',
      powerChart: 'power-chart',
      currentChart: 'current-chart',
      voltageChart: 'voltage-chart',
      costChart: 'energy-cost-chart',
      energyLoadChart: 'energy-vs-load-chart',
      table: 'table'
    };
    
    // Disconnect existing connections first
    disconnectAllWebSockets();
    
    // Small delay before reconnecting
    setTimeout(() => {
      Object.entries(endpoints).forEach(([key, endpoint]) => {
        setTimeout(() => {
          connectWebSocket(key, endpoint, craneId, dateRange);
        }, key === 'summary' ? 0 : 300);
      });
      isConnecting.current = false;
    }, 500);
  }, [connectWebSocket, disconnectAllWebSockets]);
  
  // Apply filters - called only when button is clicked
  const applyFilters = useCallback(() => {
    if (!selectedCrane) {
      showNotification('Please select a crane', 'warning');
      return;
    }
    
    // Update applied filters with current selected values
    setAppliedCrane(selectedCrane);
    setAppliedDateRange(selectedDateRange);
    setCurrentPage(1);
    
    showNotification(`Applied filters for crane: ${selectedCrane} (${getDateRangeText(selectedDateRange)})`, 'success');
  }, [selectedCrane, selectedDateRange]);
  
  const refreshAllData = useCallback(() => {
    if (!appliedCraneRef.current) {
      showNotification('No crane selected', 'warning');
      return;
    }
    
    connectAllWebSockets(appliedCraneRef.current, appliedDateRangeRef.current);
    showNotification(`Refreshing data for crane: ${appliedCraneRef.current}`, 'success');
  }, [connectAllWebSockets]);
  
  const handleWebSocketMessage = (connectionName, data) => {
    if (data.type === 'connection_status') {
      updateConnectionStatus(data);
      return;
    }
    
    switch(connectionName) {
      case 'summary':
        if (data.type === 'summary_update') {
          setSummaryData({
            totalPower: data.data?.total_power || 0,
            totalWh: data.data?.total_wh || 0,
            totalCost: data.data?.total_cost || 0,
            systemEfficiency: data.data?.system_efficiency || 0,
            energyPerTon: data.data?.energy_per_ton || 0
          });
        }
        break;
        
      case 'powerChart':
        if (data.type === 'power_chart_update') {
          updatePowerChart(data);
        }
        break;
        
      case 'currentChart':
        if (data.type === 'current_chart_update') {
          updateCurrentChart(data);
        }
        break;
        
      case 'voltageChart':
        if (data.type === 'voltage_chart_update') {
          updateVoltageChart(data);
        }
        break;
        
      case 'costChart':
        if (data.type === 'energy_cost_chart_update') {
          updateEnergyCostChart(data.data);
        }
        break;
        
      case 'energyLoadChart':
        if (data.type === 'energy_vs_load_chart_update') {
          updateEnergyLoadChart(data.data);
        }
        break;
        
      case 'table':
        if (data.type === 'table_update' && data.data) {
          setTableData(data.data.data || []);
          setTotalPages(data.data.total_pages || 1);
          setCurrentPage(data.data.page || 1);
        }
        break;
        
      default:
        break;
    }
  };
  
  const updateConnectionStatus = (data) => {
    if (!data || !data.cranes || data.cranes.length === 0) {
      setConnectionStatus({ overall: 'disconnected', message: 'Disconnected' });
      return;
    }
    
    const selectedCraneStatus = data.cranes.find(crane => crane.machine_id === appliedCraneRef.current);
    
    if (!selectedCraneStatus) {
      setConnectionStatus({ overall: 'disconnected', message: 'Disconnected' });
      return;
    }
    
    const isOnline = selectedCraneStatus.connection_status === 'online';
    setConnectionStatus({
      overall: isOnline ? 'connected' : 'disconnected',
      message: isOnline ? 'Connected' : 'Disconnected'
    });
  };
  
  const updatePowerChart = (chartData) => {
    if (!powerChartRef.current) return;
    
    try {
      if (chartData.is_incremental && chartData.data?.timestamp) {
        setPowerChartData(prev => {
          const newLabels = [...prev.labels];
          const newDatasets = [...prev.datasets];
          
          if (!newLabels.includes(chartData.data.timestamp)) {
            newLabels.push(chartData.data.timestamp);
            if (newLabels.length > 50) newLabels.shift();
            
            ['hoist', 'ct', 'lt'].forEach((motorType, idx) => {
              if (newDatasets[idx]) {
                const newData = [...newDatasets[idx].data];
                newData.push(chartData.data[motorType] || 0);
                if (newData.length > 50) newData.shift();
                newDatasets[idx].data = newData;
              }
            });
          }
          
          return { labels: newLabels, datasets: newDatasets };
        });
      } else {
        const timestamps = chartData.data?.timestamps || [];
        const colors = {
          hoist: { border: '#3498db', background: 'rgba(52, 152, 219, 0.1)' },
          ct: { border: '#2ecc71', background: 'rgba(46, 204, 113, 0.1)' },
          lt: { border: '#f39c12', background: 'rgba(243, 156, 18, 0.1)' }
        };
        
        const datasets = ['hoist', 'ct', 'lt'].map(motorType => ({
          label: `${motorType.charAt(0).toUpperCase() + motorType.slice(1)} Motor`,
          data: chartData.data?.[motorType] || [],
          borderColor: colors[motorType]?.border,
          backgroundColor: colors[motorType]?.background,
          tension: 0.4,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 5
        }));
        
        setPowerChartData({ labels: timestamps, datasets });
      }
    } catch (error) {
      console.error('Error updating power chart:', error);
    }
  };
  
  const updateCurrentChart = (chartData) => {
    if (!currentChartRef.current) return;
    
    try {
      if (chartData.is_incremental && chartData.data?.timestamp) {
        setCurrentChartData(prev => {
          const newLabels = [...prev.labels];
          const newDatasets = [...prev.datasets];
          
          if (!newLabels.includes(chartData.data.timestamp)) {
            newLabels.push(chartData.data.timestamp);
            if (newLabels.length > 50) newLabels.shift();
            
            ['hoist', 'ct', 'lt'].forEach((motorType, idx) => {
              if (newDatasets[idx]) {
                const newData = [...newDatasets[idx].data];
                newData.push(chartData.data[motorType] || 0);
                if (newData.length > 50) newData.shift();
                newDatasets[idx].data = newData;
              }
            });
          }
          
          return { labels: newLabels, datasets: newDatasets };
        });
      } else {
        const timestamps = chartData.data?.timestamps || [];
        const colors = {
          hoist: { border: '#3498db', background: 'rgba(52, 152, 219, 0.1)' },
          ct: { border: '#2ecc71', background: 'rgba(46, 204, 113, 0.1)' },
          lt: { border: '#f39c12', background: 'rgba(243, 156, 18, 0.1)' }
        };
        
        const datasets = ['hoist', 'ct', 'lt'].map(motorType => ({
          label: `${motorType.charAt(0).toUpperCase() + motorType.slice(1)} Motor`,
          data: chartData.data?.[motorType] || [],
          borderColor: colors[motorType]?.border,
          backgroundColor: colors[motorType]?.background,
          tension: 0.4,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 5
        }));
        
        setCurrentChartData({ labels: timestamps, datasets });
      }
    } catch (error) {
      console.error('Error updating current chart:', error);
    }
  };
  
  const updateVoltageChart = (chartData) => {
    if (!voltageChartRef.current) return;
    
    try {
      if (chartData.is_incremental && chartData.data?.timestamp) {
        setVoltageChartData(prev => {
          const newLabels = [...prev.labels];
          const newDatasets = [...prev.datasets];
          
          if (!newLabels.includes(chartData.data.timestamp)) {
            newLabels.push(chartData.data.timestamp);
            if (newLabels.length > 50) newLabels.shift();
            
            ['hoist', 'ct', 'lt'].forEach((motorType, idx) => {
              if (newDatasets[idx]) {
                const newData = [...newDatasets[idx].data];
                newData.push(chartData.data[motorType] || 0);
                if (newData.length > 50) newData.shift();
                newDatasets[idx].data = newData;
              }
            });
          }
          
          return { labels: newLabels, datasets: newDatasets };
        });
      } else {
        const timestamps = chartData.data?.timestamps || [];
        const colors = {
          hoist: { border: '#3498db', background: 'rgba(52, 152, 219, 0.1)' },
          ct: { border: '#2ecc71', background: 'rgba(46, 204, 113, 0.1)' },
          lt: { border: '#f39c12', background: 'rgba(243, 156, 18, 0.1)' }
        };
        
        const datasets = ['hoist', 'ct', 'lt'].map(motorType => ({
          label: `${motorType.charAt(0).toUpperCase() + motorType.slice(1)} Motor`,
          data: chartData.data?.[motorType] || [],
          borderColor: colors[motorType]?.border,
          backgroundColor: colors[motorType]?.background,
          tension: 0.4,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 5
        }));
        
        setVoltageChartData({ labels: timestamps, datasets });
      }
    } catch (error) {
      console.error('Error updating voltage chart:', error);
    }
  };
  
  const updateEnergyCostChart = (energyCostData) => {
    if (!costChartRef.current) return;
    
    const hasValidData = energyCostData && 
                         energyCostData.timestamps && 
                         energyCostData.timestamps.length > 0;
    
    setCostChartInterval(hasValidData ? 
      (energyCostData.interval_type === 'hourly' ? 'Hourly Data' : 'Daily Data') : 
      'No Data');
    
    if (hasValidData) {
      setCostChartData({
        labels: energyCostData.timestamps || [],
        datasets: [
          {
            label: 'Energy (kWh)',
            data: energyCostData.energy || [],
            backgroundColor: 'rgba(52, 152, 219, 0.7)',
            borderColor: '#2980b9',
            barPercentage: 0.6
          },
          {
            label: 'Cost (Rs)',
            data: energyCostData.cost || [],
            backgroundColor: 'rgba(46, 204, 113, 0.7)',
            borderColor: '#27ae60',
            barPercentage: 0.6
          }
        ]
      });
    } else {
      setCostChartData({
        labels: ['No Data'],
        datasets: [
          { label: 'Energy (kWh)', data: [0], backgroundColor: '#e0e0e0', barPercentage: 0.6 },
          { label: 'Cost (Rs)', data: [0], backgroundColor: '#e0e0e0', barPercentage: 0.6 }
        ]
      });
    }
  };
  
  const updateEnergyLoadChart = (energyVsLoadData) => {
    if (!energyLoadChartRef.current) return;
    
    const hasValidData = energyVsLoadData && 
                         energyVsLoadData.has_data && 
                         energyVsLoadData.energy && 
                         energyVsLoadData.load_tons &&
                         energyVsLoadData.energy.length > 0;
    
    if (hasValidData) {
      const validDataPoints = [];
      for (let i = 0; i < energyVsLoadData.load_tons.length; i++) {
        if (i < energyVsLoadData.energy.length) {
          const load = energyVsLoadData.load_tons[i];
          const energy = energyVsLoadData.energy[i];
          if (load > 0 && energy > 0) {
            validDataPoints.push({ x: load, y: energy });
          }
        }
      }
      
      if (validDataPoints.length > 0) {
        setEnergyLoadChartData({
          datasets: [{
            label: 'Energy vs Load Weight',
            data: validDataPoints,
            borderColor: '#3498db',
            backgroundColor: 'rgba(52, 152, 219, 0.1)',
            tension: 0.4,
            fill: false,
            showLine: true,
            pointBackgroundColor: '#2980b9',
            pointBorderColor: 'white',
            pointBorderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6
          }]
        });
        return;
      }
    }
    
    setEnergyLoadChartData({
      datasets: [{
        label: 'Energy vs Load Weight',
        data: [],
        borderColor: '#e0e0e0',
        backgroundColor: 'rgba(224, 224, 224, 0.1)',
        borderDash: [5, 5],
        fill: false,
        showLine: false
      }]
    });
  };
  
  const getDateRangeText = (dateRange) => {
    switch(dateRange) {
      case 'today': return 'Today';
      case 'week': return 'Last 7 Days';
      case 'month': return 'Last 30 Days';
      default: return dateRange;
    }
  };
  
  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      isUnmounting.current = true;
      disconnectAllWebSockets();
      sessionStorage.clear();
      localStorage.removeItem('isLoggedIn');
      navigate('/login');
    }
  };
  
  // FIXED: Pagination - send page change message without reconnecting
  const goToPage = (page) => {
    if (page < 1 || page > totalPages || tableData.length === 0) return;
    setCurrentPage(page);
  };
  
  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return tableData.slice(startIndex, endIndex);
  };
  
  const powerChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' } },
    scales: {
      x: { title: { display: true, text: 'Time' }, ticks: { maxRotation: 45 } },
      y: { title: { display: true, text: 'Power (kW)' }, beginAtZero: true }
    }
  };
  
  const costChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = context.raw;
            return context.dataset.label.includes('Energy') 
              ? `${value.toFixed(2)} kWh` 
              : `Rs ${value.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      x: { title: { display: true, text: costChartInterval === 'Hourly Data' ? 'Hour of Day' : 'Date' } },
      y: { title: { display: true, text: 'Energy (kWh) / Cost (Rs)' }, beginAtZero: true }
    }
  };
  
  const energyLoadChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: function(context) {
            const load = context.parsed.x;
            const energy = context.parsed.y;
            return `Load: ${load.toFixed(2)} tons, Energy: ${energy.toFixed(2)} kWh`;
          }
        }
      }
    },
    scales: {
      x: { title: { display: true, text: 'Load Weight (tons)' }, beginAtZero: true },
      y: { title: { display: true, text: 'Energy Consumption (kWh)' }, beginAtZero: true }
    }
  };
  
  // FIXED: Handle page changes without reconnecting WebSockets
  useEffect(() => {
    if (wsConnections.current.table?.readyState === WebSocket.OPEN && appliedCraneRef.current) {
      // Just send a page change message through the existing WebSocket connection
      wsConnections.current.table.send(JSON.stringify({
        type: 'change_page',
        page: currentPage,
        page_size: rowsPerPage
      }));
    }
  }, [currentPage, rowsPerPage]);
  
  // Connect WebSockets only when applied filters change (initial load or after apply button click)
  useEffect(() => {
    if (appliedCrane && initialLoadDone.current) {
      // Disconnect existing connections before creating new ones
      disconnectAllWebSockets();
      
      // Small delay to ensure cleanup
      setTimeout(() => {
        connectAllWebSockets(appliedCrane, appliedDateRange);
      }, 100);
    }
  }, [appliedCrane, appliedDateRange, connectAllWebSockets, disconnectAllWebSockets]);
  
  // Initial load - fetch cranes and set first crane
  useEffect(() => {
    isUnmounting.current = false;
    fetchCranes();
    
    return () => {
      isUnmounting.current = true;
      disconnectAllWebSockets();
    };
  }, [fetchCranes, disconnectAllWebSockets]);
  
  // Auto-connect when initial crane is set (only once)
  useEffect(() => {
    if (appliedCrane && !initialLoadDone.current) {
      initialLoadDone.current = true;
      
      // Small delay to ensure state is updated
      setTimeout(() => {
        connectAllWebSockets(appliedCrane, appliedDateRange);
      }, 500);
    }
  }, [appliedCrane, appliedDateRange, connectAllWebSockets]);
  
  const currentPageData = getPaginatedData();
  
  return (
    <div className="energy-monitoring-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className={`connection-status ${connectionStatus.overall}`}>
              <span className={`status-indicator ${connectionStatus.overall}`}></span>
              <span className="status-text">{connectionStatus.message}</span>
            </div>
            
            <div className="user-info-wrapper">
              <div className="account-dropdown">
                <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                  {orgLogo ? (
                                    <img
                                      src={orgLogo}
                                      alt="Organization Logo"
                                      style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        border: '2px solid #4361ee'
                                      }}
                                      onError={() => setOrgLogo(null)}
                                    />
                                  ) : (
                                    <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                  )}
                  <span>{sessionStorage.getItem('username') || 'User'}</span>
                  <FontAwesomeIcon icon={faCaretDown} />
                </div>
                {isDropdownOpen && (
                  <div className="account-menu show">
                    <div className="account-menu-item plan-info">
                      <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                      <span>Basic Plan</span>
                    </div>
                    <div className="account-menu-divider"></div>
                    <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faUser} /> Profile
                    </button>
                    <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                    </button>
                    <div className="account-menu-divider"></div>
                    <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                      <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Page Title */}
        <div className="page-titles">
          <h1>Energy Monitoring</h1>
          <p>Comprehensive energy consumption analysis and power metrics</p>
        </div>
        
        {/* Filter Controls */}
        <div className="log-controlss">
          <div className="filter-group">
            <label className="filter-label">Crane</label>
            <select 
              className="filter-select" 
              value={selectedCrane}
              onChange={(e) => setSelectedCrane(e.target.value)}
            >
              <option value="">Select a Crane</option>
              {cranes.map(crane => (
                <option key={crane.machine_id} value={crane.machine_id}>
                  {crane.machine_name} ({crane.machine_id})
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label className="filter-label">Date Range</label>
            <select 
              className="filter-select" 
              value={selectedDateRange}
              onChange={(e) => setSelectedDateRange(e.target.value)}
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>
          
          <div className="action-buttonsa" style={{ 
                        marginTop: '30px'
                        }}>
            <button className="btn btn-primary" onClick={applyFilters}>
              <FontAwesomeIcon icon={faFilter} /> Apply Filters
            </button>
            <button className="btn btn-refresh" onClick={refreshAllData}>
              <FontAwesomeIcon icon={faSyncAlt} /> Refresh All
            </button>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="summarycards">
          <div className="summarycard">
            <div className="summarycardicon"><FontAwesomeIcon icon={faBolt} /></div>
            <div className="summarycardvalue">{summaryData.totalPower.toFixed(2)} kWh</div>
            <div className="summarycardlabel">Total Energy Consumption</div>
            <div style={{ fontSize: '0.8rem', color: '#95a5a6', marginTop: '4px' }}>{summaryData.totalWh.toFixed(2)} Wh</div>
          </div>
          <div className="summarycard">
            <div className="summarycardicon"><FontAwesomeIcon icon={faMoneyBillWave} /></div>
            <div className="summarycardvalue">Rs {summaryData.totalCost.toFixed(2)}</div>
            <div className="summarycardlabel">Total Energy Cost</div>
          </div>
          <div className="summarycard">
            <div className="summarycardicon"><FontAwesomeIcon icon={faBalanceScale} /></div>
            <div className="summarycardvalue">{summaryData.energyPerTon.toFixed(2)} kWh/T</div>
            <div className="summarycardlabel">Energy per Ton</div>
          </div>
          <div className="summarycard">
            <div className="summarycardicon"><FontAwesomeIcon icon={faPercentage} /></div>
            <div className="summarycardvalue">{summaryData.systemEfficiency}%</div>
            <div className="summarycardlabel">System Efficiency</div>
          </div>
        </div>
        
        {/* Chart Grid */}
        <div className="chart-grid">
          <div className="chart-containera">
            <div className="chart-title">Power Consumption</div>
            <div className="chart-wrapper">
              <Line ref={powerChartRef} data={powerChartData} options={powerChartOptions} />
            </div>
          </div>
          <div className="chart-containera">
            <div className="chart-title">Current Draw</div>
            <div className="chart-wrapper">
              <Line ref={currentChartRef} data={currentChartData} options={{ ...powerChartOptions, scales: { ...powerChartOptions.scales, y: { title: { display: true, text: 'Current (A)' }, beginAtZero: true } } }} />
            </div>
          </div>
          <div className="chart-containera">
            <div className="chart-title">Voltage Levels</div>
            <div className="chart-wrapper">
              <Line ref={voltageChartRef} data={voltageChartData} options={{ ...powerChartOptions, scales: { ...powerChartOptions.scales, y: { title: { display: true, text: 'Voltage (V)' }, beginAtZero: true } } }} />
            </div>
          </div>
          <div className="chart-containera">
            <div className="chart-title">Energy vs Load Weight</div>
            <div className="chart-wrapper">
              <Scatter ref={energyLoadChartRef} data={energyLoadChartData} options={energyLoadChartOptions} />
            </div>
          </div>
        </div>
        
        <div className="chart-grid-1">
          <div className="chart-containera">
            <div className="chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '8px' }}>
              <div className="chart-title" style={{ marginBottom: 0 }}>Energy & Cost Analysis</div>
              <div className="chart-badge" style={{ background: '#3498db', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600 }}>
                {costChartInterval}
              </div>
            </div>
            <div className="chart-wrapper">
              <Bar ref={costChartRef} data={costChartData} options={costChartOptions} />
            </div>
          </div>
        </div>
        
        {/* Energy Log Table */}
        <div className="log-table-container">
          <table className="log-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Crane ID</th>
                <th>Motor Type</th>
                <th>Power (kW)</th>
                <th>Current (A)</th>
                <th>Voltage (V)</th>
              </tr>
            </thead>
            <tbody>
              {currentPageData.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', color: '#95a5a6', padding: '30px' }}>
                    <FontAwesomeIcon icon={faInfoCircle} /> Connect to a crane to view real-time energy data
                  </td>
                </tr>
              ) : (
                currentPageData.map((item, index) => (
                  <tr key={index}>
                    <td>{new Date(item.timestamp).toLocaleString()}</td>
                    <td>{item.machine_id || appliedCrane}</td>
                    <td>{item.motor_type || '--'}</td>
                    <td>{(item.power || 0).toFixed(1)}</td>
                    <td>{(item.current || 0).toFixed(1)}</td>
                    <td>{(item.voltage || 0).toFixed(1)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          
          <div className="table-footer">
            <div className="pagination">
              <button className="pagination-btn" onClick={() => goToPage(1)} disabled={currentPage === 1 || tableData.length === 0}>
                <FontAwesomeIcon icon={faAngleDoubleLeft} />
              </button>
              <button className="pagination-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1 || tableData.length === 0}>
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <div className="pagination-info">
                <span>Page {currentPage} of {totalPages} ({tableData.length} items)</span>
              </div>
              <button className="pagination-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages || tableData.length === 0}>
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
              <button className="pagination-btn" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages || tableData.length === 0}>
                <FontAwesomeIcon icon={faAngleDoubleRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default EnergyMonitoring;